import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { formatInTimeZone } from 'date-fns-tz'

import { apiClient } from '@/lib/api/client'
import type { OpsActiveEvent, OpsFeedRow, OpsOverview, OpsVolumeByCurrency } from '@/lib/api/types'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { TransactionTypeBadge } from '@/components/transaction-type-badge'

export const Route = createFileRoute('/_authenticated/ops/pulse')({
  // Superuser gating lives on the /ops layout route (ops.tsx).
  component: PulsePage,
})

const REFRESH_MS = 30_000

const WINDOWS = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1h', value: '60m' },
  { label: '24h', value: '1440m' },
]

const PHASE_STYLES: Record<OpsActiveEvent['phase'], string> = {
  live: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  ended: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
}

/** "$37.87" — bare dollar sign; the currency CODE lives in the tile sub line.
 *  Null-safe: the deployed API may predate a field, and a missing number must
 *  render as '—', never crash the dashboard. */
function narrowCurrency(value: number | null | undefined, code: string): string {
  if (value == null) return '—'
  try {
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 2,
    })
  } catch {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${code}`.trim()
  }
}

function currency(value: number | null | undefined, code: string): string {
  if (value == null) return '—'
  try {
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    })
  } catch {
    // Unknown/blank currency code — show the number rather than crashing.
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${code}`.trim()
  }
}

/** "CA$1,234 · US$56" — one figure per currency, never cross-summed. */
function perCurrency(
  rows: OpsVolumeByCurrency[] | undefined,
  pick: (row: OpsVolumeByCurrency) => number | null | undefined,
): string {
  if (!rows || rows.length === 0) return '—'
  return rows.map((row) => currency(pick(row), row.currency)).join(' · ')
}

function relativeTime(iso: string, now: number): string {
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  return `${Math.floor(seconds / 3600)}h ago`
}

function windowLabel(minutes: number): string {
  if (minutes < 60) return `last ${minutes}m`
  if (minutes === 60) return 'last hour'
  if (minutes % 60 === 0) return `last ${minutes / 60}h`
  return `last ${minutes}m`
}

function PulsePage() {
  const { token } = useAuth()
  const [windowValue, setWindowValue] = useState('15m')
  // Render-pure clock for the "updated Ns ago" stamp and feed timestamps: a
  // dashboard that silently stops refreshing is worse than one that admits
  // it, and reading Date.now() during render is impure (lint-enforced).
  const [now, setNow] = useState(() => Date.now())

  const { data, isLoading, isFetching, error, dataUpdatedAt, refetch } = useQuery<OpsOverview>({
    queryKey: ['ops-overview', windowValue],
    queryFn: () => apiClient.getOpsOverview(token ?? '', windowValue),
    enabled: !!token,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    // Keep the last good payload visible while a refetch is in flight or
    // failing, so the numbers don't blank out mid-event.
    placeholderData: (previous) => previous,
  })

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const tiles = data?.tiles
  // One currency (the norm): bare number in the tile, code in the sub line —
  // the prefixed figure was overflowing the card. Mixed currencies keep the
  // explicit per-currency join; ambiguity beats overflow there.
  const singleCurrency =
    tiles && tiles.volume_by_currency.length === 1 ? tiles.volume_by_currency[0] : null
  const stale = dataUpdatedAt > 0 && now - dataUpdatedAt > REFRESH_MS * 2

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Pulse</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Live activity across every event and organization.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                onClick={() => setWindowValue(w.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  windowValue === w.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-neutral-900 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800',
                )}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Freshness / error strip */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-4 text-xs">
        {dataUpdatedAt > 0 && (
          <span className={cn('font-medium', stale ? 'text-amber-700 dark:text-amber-400' : 'text-neutral-500 dark:text-neutral-400')}>
            {stale ? 'Stale — last updated ' : 'Updated '}
            {relativeTime(new Date(dataUpdatedAt).toISOString(), now)}
          </span>
        )}
        {error && (
          <span className="text-amber-700 dark:text-amber-400">
            Refresh failed ({(error as Error).message}) — showing last known values.
          </span>
        )}
        {data?.window_clamped && (
          <span className="text-amber-700 dark:text-amber-400">
            Window capped at {data.window_minutes}m.
          </span>
        )}
      </div>

      {isLoading && !data ? (
        <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading…</div>
      ) : (
        <>
          {/* Tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <Tile label="Transactions" value={tiles ? tiles.transactions.toLocaleString() : '—'} sub={windowLabel(data?.window_minutes ?? 0)} />
            <Tile label="Orders" value={tiles ? tiles.orders.toLocaleString() : '—'} sub={windowLabel(data?.window_minutes ?? 0)} />
            <Tile label="Customers" value={tiles ? tiles.customers.toLocaleString() : '—'} sub="any transaction type" />
            <Tile
              label="Volume"
              value={
                singleCurrency
                  ? narrowCurrency(singleCurrency.volume, singleCurrency.currency)
                  : perCurrency(tiles?.volume_by_currency, (row) => row.volume)
              }
              sub={singleCurrency ? `${singleCurrency.currency} · gross token reloads` : 'gross token reloads, per currency'}
            />
            <Tile
              label="Avg. Reload"
              value={
                singleCurrency
                  ? narrowCurrency(singleCurrency.avg_reload, singleCurrency.currency)
                  : perCurrency(tiles?.volume_by_currency, (row) => row.avg_reload)
              }
              sub={singleCurrency ? `${singleCurrency.currency} · add-credit only` : 'add-credit only, per currency'}
            />
            <Tile
              label="Avg. Order"
              value={
                singleCurrency
                  ? narrowCurrency(singleCurrency.avg_order, singleCurrency.currency)
                  : perCurrency(tiles?.volume_by_currency, (row) => row.avg_order)
              }
              sub={singleCurrency ? `${singleCurrency.currency} · token orders` : 'token orders, per currency'}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Active events */}
            <div className="xl:col-span-2 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
              <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">
                  Active events
                </h2>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  live now, or transacting in the {windowLabel(Math.max(60, data?.window_minutes ?? 60))}
                </span>
              </div>

              {data && data.active_events.length === 0 ? (
                <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">
                  Nothing active right now.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-800">
                        <th className="text-left font-medium px-5 py-2">Event</th>
                        <th className="text-left font-medium px-3 py-2">Venue time</th>
                        <th className="text-right font-medium px-3 py-2">Txns</th>
                        <th className="text-right font-medium px-3 py-2">Orders</th>
                        <th className="text-right font-medium px-3 py-2">Customers</th>
                        <th className="text-right font-medium px-5 py-2">Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.active_events.map((event) => (
                        <EventRow key={event.id} event={event} generatedAt={data.generated_at} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tiles && tiles.orders_unattributed > 0 && (
                <p className="px-5 py-3 text-xs text-neutral-500 dark:text-neutral-400 border-t border-neutral-200 dark:border-neutral-800">
                  {tiles.orders_unattributed} order(s) in this window have no vendor/event link, so
                  the per-event rows won't sum to the Orders tile.
                </p>
              )}
            </div>

            {/* Feed */}
            <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
              <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
                <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">
                  Latest transactions
                </h2>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  most recent {data?.feed.length ?? 0}, all events
                </span>
              </div>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800 max-h-[520px] overflow-y-auto">
                {data?.feed.map((row) => <FeedRow key={row.id} row={row} now={now} />)}
                {data && data.feed.length === 0 && (
                  <li className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">
                    No transactions yet.
                  </li>
                )}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold text-neutral-900 dark:text-white tabular-nums">
        {value}
      </p>
      <p className="mt-0.5 text-xs text-neutral-400 dark:text-neutral-500">{sub}</p>
    </div>
  )
}

function EventRow({ event, generatedAt }: { event: OpsActiveEvent; generatedAt: string }) {
  let venueTime = '—'
  try {
    venueTime = formatInTimeZone(new Date(generatedAt), event.timezone, 'h:mm a')
  } catch {
    venueTime = '—'
  }

  return (
    <tr className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
      <td className="px-5 py-3">
        <Link
          to="/events/$eventId/dashboard"
          params={{ eventId: String(event.id) }}
          className="flex items-center gap-3 group"
        >
          {event.logo ? (
            <img src={event.logo} alt="" className="w-8 rounded object-cover shrink-0" />
          ) : (
            <span className="h-8 w-8 rounded bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xs font-semibold text-neutral-500 dark:text-neutral-300 shrink-0">
              {event.name.charAt(0)}
            </span>
          )}
          <span className="min-w-0">
            <span className="block font-medium text-neutral-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate max-w-[280px]">
              {event.name}
            </span>
            <span className="flex items-center gap-2 mt-0.5">
              <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium uppercase', PHASE_STYLES[event.phase])}>
                {event.phase}
              </span>
              {event.organization && (
                <span className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  {event.organization}
                </span>
              )}
            </span>
          </span>
        </Link>
      </td>
      <td className="px-3 py-3 text-neutral-600 dark:text-neutral-400 whitespace-nowrap tabular-nums">
        {venueTime}
      </td>
      <td className="px-3 py-3 text-right tabular-nums text-neutral-900 dark:text-white">
        {event.transactions.toLocaleString()}
      </td>
      <td className="px-3 py-3 text-right tabular-nums text-neutral-900 dark:text-white">
        {event.orders.toLocaleString()}
      </td>
      <td className="px-3 py-3 text-right tabular-nums text-neutral-900 dark:text-white">
        {event.customers.toLocaleString()}
      </td>
      <td className="px-5 py-3 text-right tabular-nums font-medium text-neutral-900 dark:text-white">
        {currency(event.volume, event.currency)}
      </td>
    </tr>
  )
}

function FeedRow({ row, now }: { row: OpsFeedRow; now: number }) {
  return (
    <li className="px-5 py-2.5 flex items-center gap-3">
      <span className="w-24 shrink-0">
        <TransactionTypeBadge action={row.action} amount={row.tokens} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-neutral-900 dark:text-white truncate">
          {row.user_name ?? 'Unknown user'}
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
          {row.event_name ?? 'No event'}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm tabular-nums text-neutral-900 dark:text-white">
          {row.value != null && row.value !== 0
            ? `${row.value > 0 ? '+' : ''}${narrowCurrency(row.value, row.currency ?? '')}`
            : row.tokens !== 0
              ? `${row.tokens > 0 ? '+' : ''}${row.tokens.toLocaleString()}`
              : '—'}
        </p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 whitespace-nowrap">
          {relativeTime(row.timestamp, now)}
        </p>
      </div>
    </li>
  )
}
